import * as React from 'react';

import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';

import { setLocation } from 'js/lib/coursera.redirect';
import { get as getUser } from 'js/lib/user';

import { PRODUCT_TYPES } from 'bundles/enterprise-legacy-learner-home/constants/ProgramActionConstants';
import type { PROGRAM_JOIN_ERRORS } from 'bundles/enterprise-legacy-learner-home/constants/ProgramJoinModalErrors';
import ProgramActionApiManager from 'bundles/enterprise-legacy-learner-home/models/ProgramActionApiManager';
import { useNaptime } from 'bundles/naptimejs';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useEnterpriseCoursePageData } from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseBffCourseContext';
import { useEnterpriseSpecializationPageData } from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseBffSpecializationContext';
import JoinProgramMutation from 'bundles/unified-description-page-common/queries/ProgramJoin.graphql';
import type {
  ProgramJoinMutation,
  ProgramJoinMutationVariables,
} from 'bundles/unified-description-page-common/queries/__generated__/ProgramJoin';

import _t from 'i18n!nls/xdp';

type EnrollIntoCourseFn = (config?: { isAutoEnrollment?: boolean }) => Promise<null>;

// Tests for this are covered in EnterpriseLearnerFlowOptimizationDataProvider.test.tsx
const useEnterpriseCourseEnrollment = (onActionSuccess: () => void) => {
  const [joinProgram] = useMutation<ProgramJoinMutation, ProgramJoinMutationVariables>(JoinProgramMutation, {
    context: { clientName: 'gatewayGql' },
  });
  const [isEnrolling, setIsEnrolling] = React.useState(false);
  const [error, setError] = React.useState<string>('');
  const [programJoinErrorCode, setProgramJoinErrorCode] = React.useState<
    keyof typeof PROGRAM_JOIN_ERRORS | undefined
  >();
  const { naptime } = useNaptime(() => ({}));
  const userId = getUser().id;
  const program = useProgram();
  const programId = program?.id;
  const coursePageData = useEnterpriseCoursePageData();
  const specializationPageData = useEnterpriseSpecializationPageData();
  const programSlug = program?.metadata.slug;

  const productId = coursePageData?.id ? coursePageData.id : (specializationPageData?.id as string);
  const productType = coursePageData?.id ? PRODUCT_TYPES.COURSE : PRODUCT_TYPES.SPECIALIZATION;
  const courseId = coursePageData?.id || specializationPageData?.courses?.[0]?.id;
  // In case of auto-enrollment, the course id is not needed,
  // as triggering the program enrollment will automatically enroll the user into the first course
  const enrollIntoCourse = React.useCallback<EnrollIntoCourseFn>(
    async (config = {}) => {
      if (!programId || isEnrolling) {
        return null;
      }
      setProgramJoinErrorCode(undefined);
      setError('');
      setIsEnrolling(true);
      const { isAutoEnrollment = false } = config;
      const genericFailedToEnrollMessage = _t('Failed to enroll. Please refresh and try again later');
      try {
        const { data: programJoinData } = await joinProgram({
          variables: { input: { programId, ...(!isAutoEnrollment && { courseId }) } },
        });
        if (!programJoinData) {
          throw new Error('No data was returned from the graphql operation');
        }
        if (programJoinData.ProgramMembership_Join.__typename === 'ProgramMembership_JoinResultFailure') {
          const erroCode = programJoinData.ProgramMembership_Join.error;
          if (erroCode !== 'ALREADY_A_MEMBER') {
            setProgramJoinErrorCode(erroCode as keyof typeof PROGRAM_JOIN_ERRORS);
            setIsEnrolling(false);
            Sentry.captureException({ erroCode, programId, courseId });
            return null;
          }
        }
      } catch (err) {
        setError(genericFailedToEnrollMessage);
        setIsEnrolling(false);
        Object.assign(err, { enrollParams: { programId, productType, productId } });
        Sentry.captureException(err, {
          extra: { message: '[Learner flow optimization] Error enrolling into product' },
        });
        return null;
      }
      const programActionApi = new ProgramActionApiManager({ programId, naptime, userId });

      const enrollPromise =
        productType === PRODUCT_TYPES.SPECIALIZATION
          ? programActionApi.getEnrollInS12nPromise({ firstCourseId: null, s12nId: productId, collectionId: null })
          : programActionApi.getEnrollInCoursePromise({ courseId: productId, collectionId: null });

      try {
        await enrollPromise;
        onActionSuccess();
        return null;
      } catch (err) {
        const errorCode = err?.responseJSON?.errorCode;
        // If they are already enrolled, continue with sending them to the course
        if (errorCode === 'ALREADY_ENROLLED_ERROR') {
          onActionSuccess();
          return null;
        } else if (errorCode === 'ENROLL_IN_COURSE_ERROR') {
          // This means the course is completed
          setLocation(`/programs/${programSlug}/my-learning?myLearningTab=IN_PROGRESS`);
          return null;
        }
        setIsEnrolling(false);
        setError(genericFailedToEnrollMessage);
        Object.assign(err, { enrollParams: { programId, productType, productId } });
        Sentry.captureException(err, {
          extra: { message: '[Learner flow optimization] Error enrolling into product' },
        });
        return null;
      }
    },
    // naptime reference changes multiple times during re-renders, which results into this useCallback re-executing again.
    // Which results into cascade effect of useCallbacks re-executing down the line.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [programId, productType, productId, courseId]
  );
  return [enrollIntoCourse, { error, programJoinErrorCode, isEnrolling }] as const;
};

export default useEnterpriseCourseEnrollment;
